import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Compass, Upload, Eye, Sparkles, LineChart, Target, Lightbulb, Scale, ArrowRight, ArrowLeft, Wallet, ShieldCheck, Loader2 } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import { useDemoStatus } from '@/hooks/useDemoStatus';
import { PriyaIntroModal } from '@/components/onboarding/PriyaIntroModal';

/**
 * Phase 55 follow-up #6 — One-time welcome tour for first-time users.
 *
 * Why this exists:
 *   2120 isn't a budget app. New users land expecting Mint / YNAB / a
 *   spreadsheet and instead see a clarity-focused dashboard. Without
 *   context many think "this looks nice, now what?". The tour answers
 *   four questions in six screens:
 *     1. What is this?
 *     2. What should I do first?
 *     3. How complete is my picture?
 *     4. What value will I keep getting?
 *
 * Behaviour:
 *   - Renders when `profile.welcome_tour_completed_at` is NULL.
 *   - No dismiss button, no Escape close, no overlay click close. The
 *     user must click Next through all six slides and Get started on
 *     the last. This is intentional per the spec.
 *   - On completion, calls POST /api/profile/welcome-tour-complete to
 *     stamp the profile, and mirrors a localStorage key so re-opens in
 *     the same session don't re-trigger if the profile fetch is slow.
 *   - On the final slide, "Get started" closes the modal AND navigates
 *     to /import (the most valuable first action).
 */

const SLIDES = [
  {
    id: 'welcome',
    icon: Compass,
    image: '/tour/welcome.png',
    imageAlt: 'A compass and constellation, evoking finding your bearings',
    eyebrow: 'Welcome to 2120',
    headline: 'Financial clarity for modern life.',
    body: (
      <>
        <p>2120 helps you understand where your money goes, what you&rsquo;re building, and how your financial life is changing over time.</p>
        <p className="text-sm text-muted-foreground mt-3">Not budgeting. Not investment advice. Just clarity.</p>
      </>
    ),
  },
  {
    id: 'accounts',
    icon: Wallet,
    image: '/tour/accounts.png',
    imageAlt: 'A neat stack of statements bound with a gold ribbon',
    eyebrow: 'Step 1',
    headline: 'Start with your accounts.',
    body: (
      <>
        <p>Upload a bank statement, or add an account manually. The more accounts you add, the more complete your picture becomes.</p>
        <p className="text-sm text-muted-foreground mt-3">Most banks export PDF or CSV statements straight from net banking. We&rsquo;ll parse them for you.</p>
      </>
    ),
  },
  {
    id: 'honesty',
    icon: Eye,
    image: '/tour/honesty.png',
    imageAlt: 'A partially completed puzzle with a golden piece being placed',
    eyebrow: 'A small honest thing',
    headline: 'Your picture may be incomplete, and that’s fine.',
    body: (
      <>
        <p>2120 only knows about the accounts and statements you share. We&rsquo;ll be honest about that, and show how complete your financial picture is as you add more.</p>
        <p className="text-sm text-muted-foreground mt-3">Most apps pretend they know everything. We&rsquo;d rather tell you the truth and build trust over time.</p>
      </>
    ),
  },
  {
    // Phase 70 — Trust-audit (B3): an explicit privacy/data-handling
    // promise placed BEFORE the user starts uploading statements. Beta
    // users in regulated/security-conscious markets (US, Singapore, GCC)
    // bounce specifically because no app screen explains how their
    // financial data will be treated. This slide makes the four
    // commitments most users actually care about, in plain language.
    id: 'privacy',
    icon: ShieldCheck,
    image: '/tour/privacy.png',
    imageAlt: 'A leaf-motif shield with a golden key, above a small vault',
    eyebrow: 'How we treat your data',
    headline: 'Your money is your business. Not ours to sell.',
    body: (
      <>
        <ul className="space-y-2.5 text-sm">
          <li className="flex items-start gap-2.5">
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
            <span><span className="font-medium text-foreground">No ads, ever.</span> We don&rsquo;t sell, share, or rent your financial data to anyone.</span>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
            <span><span className="font-medium text-foreground">You&rsquo;re the customer.</span> Our business is your subscription, not your attention.</span>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
            <span><span className="font-medium text-foreground">Encrypted in transit and at rest.</span> Your statements live in your private account, scoped to you alone.</span>
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'discover',
    icon: Sparkles,
    image: '/tour/discover.png',
    imageAlt: 'Four small vignettes: line chart, compass, target, notebook',
    eyebrow: 'What you’ll discover',
    headline: 'Four lenses on your money.',
    body: null,
    cards: [
      { icon: LineChart, label: 'Spending', desc: 'Where your money actually goes.' },
      { icon: Compass,   label: 'Trends',   desc: 'How things change month to month.' },
      { icon: Target,    label: 'Goals',    desc: 'What you&rsquo;re building toward.' },
      { icon: Lightbulb, label: 'Reflections', desc: 'Notes that help you make sense of it.' },
    ],
  },
  {
    id: 'over_time',
    icon: LineChart,
    image: '/tour/over_time.png',
    imageAlt: 'A sapling growing into a young tree, then an established tree with a golden leaf',
    eyebrow: 'Clarity grows over time',
    headline: 'A single statement helps. A year of them tells a story.',
    body: (
      <>
        <p>The more history you add, the more useful your trends, reflections, and projections become.</p>
        <div className="mt-4 flex items-center gap-3 text-sm">
          <div className="rounded-xl border border-border/60 bg-muted/40 px-3 py-2 flex-1 text-center">
            <div className="font-display text-base font-semibold">1 statement</div>
            <div className="text-xs text-muted-foreground">A snapshot.</div>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="rounded-xl border border-border/60 bg-muted/40 px-3 py-2 flex-1 text-center">
            <div className="font-display text-base font-semibold">3 months</div>
            <div className="text-xs text-muted-foreground">Patterns emerge.</div>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="rounded-xl border border-primary/40 bg-primary/8 px-3 py-2 flex-1 text-center">
            <div className="font-display text-base font-semibold">12 months</div>
            <div className="text-xs text-muted-foreground">A story.</div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'first_action',
    icon: Upload,
    image: '/tour/first_action.png',
    imageAlt: 'A single statement lifted upward by a delicate golden thread, framed by an evergreen arch',
    eyebrow: 'Choose how you want to start',
    headline: 'Explore with sample data, or import your own.',
    body: (
      <>
        <p>Not ready to upload yet? Try 2120 with our demo profile <span className="font-medium text-foreground">Priya from Dubai</span> — a multi-currency snapshot with pre-loaded accounts, transactions, wealth, loans and goals.</p>
        <p className="text-sm text-muted-foreground mt-3">Or jump straight in with your own bank statement. Either way, you can always switch later.</p>
      </>
    ),
    finalCta: 'Import a statement',
    finalNavigateTo: '/import',
    // Phase 84.22 flag: render the two-option fork instead of a single button.
    forkDemoOrImport: true,
  },
];

// Phase 84.11 — bumped to `v2:` after the illustrated tour rewrite.
// Existing users who completed the icon-based tour will now see the
// illustrated version exactly once because their previous localStorage
// key (unversioned prefix) no longer matches this check. Legacy backend
// timestamp `profile.welcome_tour_completed_at` is intentionally IGNORED
// for the reset — see the useEffect below.
const STORAGE_KEY_PREFIX = 'twoone20:welcome_tour_v2_completed:';
// Legacy key from the pre-illustration tour, kept only so we could migrate
// or read from it in the future if needed. Not checked when deciding
// whether to show the tour.
const LEGACY_STORAGE_KEY_PREFIX = 'twoone20:welcome_tour_completed:';

export function WelcomeTour() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { seedDemo, loading: demoLoading } = useDemoStatus();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [completing, setCompleting] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [priyaModalOpen, setPriyaModalOpen] = useState(false);

  // Show the tour when:
  //   • we have an authenticated user
  //   • their profile is loaded
  //   • the profile's welcome_tour_completed_at field is null/missing
  //   • AND the defensive localStorage mirror also says not-yet-completed
  // The localStorage check prevents a brief flash of the tour while the
  // backend write is in flight on the last slide.
  //
  // Phase 82.12 hardening: this effect used to only ever SET open=true.
  // In tab-navigation races (route change fires a fresh AuthContext
  // profile fetch and mid-flight the useEffect re-runs on a stale profile
  // shape) the tour occasionally lingered as a stuck overlay after
  // completion, blocking downstream clicks (e.g. "Add commitment" on
  // /recurring). We now derive `shouldShow` and ALWAYS sync `open` to
  // it, so once localStorage OR the profile confirms completion the
  // dialog reliably closes even if the user has since navigated.
  useEffect(() => {
    if (!user?.id || !profile) return;
    const localKey = `${STORAGE_KEY_PREFIX}${user.id}`;
    const localDone = typeof window !== 'undefined' && window.localStorage.getItem(localKey);
    // Phase 84.17 — cross-device fix. v3.0.13 deliberately ignored the
    // backend `welcome_tour_completed_at` stamp to force everyone to
    // re-see the illustrated (v2) tour once. That reset served its
    // purpose weeks ago; keeping the backend stamp ignored means the
    // tour now replays on ANY device where localStorage is empty:
    //   • fresh sign-in on a second device
    //   • incognito / private browsing
    //   • Safari's 7-day inactivity purge
    //   • any user-initiated site-data clear
    //
    // We now trust EITHER signal:
    //   • localStorage v2 key present (fast local check), OR
    //   • profile.welcome_tour_completed_at populated on the backend
    //     (survives localStorage wipes, propagates across devices).
    // On completion `completeAndClose` writes to BOTH, so the two
    // stay in sync going forward. To force a future refresh of the
    // tour for all users, bump STORAGE_KEY_PREFIX to a new suffix
    // (e.g. `_v3_completed:`) — that alone triggers a one-time
    // re-show without needing another server-side sweep.
    const profileDone = !!profile.welcome_tour_completed_at;
    // v3.7.4 Phase 84.28.12 — force-open when the Replay tour button
    // (Settings → Help & Orientation) navigates with ?replayTour=1.
    // Without this, profile refresh timing meant `profileDone` was
    // often still true when the dashboard mounted, so the tour never
    // reopened despite the toast saying it would.
    const forceReplay = typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('replayTour') === '1';
    const shouldShow = forceReplay || (!localDone && !profileDone);
    setOpen(shouldShow);
    if (shouldShow) setIndex(0);
    if (forceReplay) {
      // Consume the query param so a hard refresh doesn't reopen it
      // forever. Uses history.replaceState so React Router state isn't
      // disturbed.
      try {
        const clean = window.location.pathname;
        window.history.replaceState({}, '', clean);
      } catch { /* ignore */ }
    }
  }, [user?.id, profile]);

  const total = SLIDES.length;
  const slide = SLIDES[index];
  const isLast = index === total - 1;

  const goBack = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(total - 1, i + 1));

  const completeAndClose = async () => {
    setCompleting(true);
    // Mirror locally first so the tour can't re-appear even if the API
    // call fails / is slow on the next route load.
    try {
      const localKey = `${STORAGE_KEY_PREFIX}${user.id}`;
      window.localStorage.setItem(localKey, new Date().toISOString());
    } catch { /* private mode etc. */ }
    // Close the dialog IMMEDIATELY before we do any awaited work. Doing
    // this first prevents a Radix DOM artifact where the portal lingers
    // with data-state='open' after an intervening state update between
    // the completion click and setOpen(false) (surfaced in iteration_81
    // as a low-priority cosmetic issue — the tour did not re-appear on
    // navigation, but the DOM node stayed with data-state='open' until
    // the next render tick because the state batch ordering below).
    setOpen(false);
    // Best-effort backend stamp + profile refresh — fire-and-forget so
    // slow network latency does not delay the visible dismissal.
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/welcome-tour-complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
          });
        }
      } catch { /* silently swallow — localStorage already mirrors */ }
      // Refetch profile so the useEffect sees welcome_tour_completed_at
      // populated and stops re-opening the dialog on next route change.
      // Without this, browser-automation tools (and private-mode users)
      // that clear localStorage between actions would still see the tour
      // re-open because only the localStorage mirror was updated.
      try { await refreshProfile?.(); } catch { /* non-fatal */ }
    })();
    setCompleting(false);
    // Send them straight to the most valuable first action.
    if (slide?.finalNavigateTo) navigate(slide.finalNavigateTo);
  };

  // The Dialog primitive's onOpenChange fires on overlay click and Escape.
  // We intercept those: while the tour is open, the only way out is the
  // explicit "Get started" button on the last slide.
  const Icon = slide?.icon || Compass;

  return (
    <>
    <PriyaIntroModal open={priyaModalOpen} onOpenChange={setPriyaModalOpen} />
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Refuse to close unless we initiated it from completeAndClose().
        if (next === false) {
          // no-op — must click through
          return;
        }
        setOpen(next);
      }}
    >
      <DialogContent
        className="sm:max-w-5xl lg:max-w-6xl w-[calc(100vw-1.5rem)] sm:w-auto max-h-[92vh] overflow-y-auto rounded-2xl p-0 [&>button[type='button']]:hidden"
        // Block the X close (we don't render one) and outside-click close.
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="welcome-tour-dialog"
      >
        {/* Defense-in-depth: tiny invisible overlay where the primitive's
            built-in Close button sits. The Tailwind selector above hides
            it visually; this blocker stops the rare case where a focused
            keyboard user could Tab onto an invisible button. */}
        <div
          aria-hidden
          className="absolute right-3 top-3 h-8 w-8 z-10 pointer-events-auto"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          data-testid="welcome-tour-close-blocker"
        />

        {/* Phase 84.11 — Two-column layout: illustration on the LEFT
            (desktop) or a compact top strip (mobile). The images are
            pre-generated Nano Banana PNGs sitting in /public/tour/.
            Phase 84.14 mobile fix: on <md screens the illustration is
            capped at max-h-[160px] with reduced padding so the
            eyebrow/headline/body + Next button all remain visible on a
            standard phone viewport (iPhone SE / 14 = 667–852px tall)
            without needing to scroll. */}
        <div className="grid md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          {/* Illustration column — full-bleed strip on mobile, tall
              left panel on desktop. */}
          <div
            className="relative bg-[#F5F0E2] flex items-center justify-center px-4 md:px-6 py-4 md:py-10 md:min-h-[520px] border-b md:border-b-0 md:border-r border-border/40"
            data-testid="welcome-tour-illustration"
          >
            {slide.image ? (
              <img
                src={slide.image}
                alt={slide.imageAlt || ''}
                // Mobile: cap the render HEIGHT so the illustration
                // never dominates the viewport. Desktop: cap the WIDTH
                // and let the image find its own height in a tall
                // column.
                className="max-h-[160px] md:max-h-none w-auto md:w-full md:max-w-[360px] h-auto select-none pointer-events-none object-contain"
                draggable={false}
                loading="eager"
                key={slide.id /* re-mount on slide change so the fade transition fires */}
              />
            ) : (
              // Defensive fallback — should never fire in practice since
              // every slide ships with an illustration in v2.
              <div className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-primary/8 border border-border/60 flex items-center justify-center">
                <Icon className="h-8 w-8 md:h-10 md:w-10 text-primary" />
              </div>
            )}
          </div>

          {/* Text column — eyebrow · headline · body · (optional) card grid */}
          <div className="flex flex-col">
            <div className="px-5 md:px-7 pt-5 md:pt-7 pb-2">
              <div className="flex items-center gap-2 text-[11px] md:text-xs uppercase tracking-wide text-muted-foreground">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <span>{slide.eyebrow}</span>
              </div>
              <h2 className="font-display text-xl md:text-[26px] font-semibold tracking-tight mt-2 md:mt-3 leading-snug" data-testid="welcome-tour-headline">
                {slide.headline}
              </h2>
            </div>

            <div className="px-5 md:px-7 py-3 md:py-4 space-y-3 text-[14px] md:text-[15px] leading-relaxed text-foreground/85 flex-1">
              {slide.body}
              {slide.cards && (
                <div className="grid grid-cols-2 gap-2.5 md:gap-3 mt-1">
                  {slide.cards.map((c) => {
                    const CIcon = c.icon;
                    return (
                      <div
                        key={c.label}
                        className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2.5"
                      >
                        <div className="flex items-center gap-2">
                          <CIcon className="h-4 w-4 text-primary" />
                          <div className="font-medium text-sm">{c.label}</div>
                        </div>
                        <div
                          className="text-xs text-muted-foreground mt-1"
                          // Allow the &rsquo; escape in card descriptions.
                          dangerouslySetInnerHTML={{ __html: c.desc }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer: dots + nav. sticky-bottom on mobile so it stays
                visible even if the body ever overflows on a tiny screen.

                Phase 84.22.1 responsive fix: on the last slide we render
                THREE buttons (Back · Try demo · Import own). At narrow
                widths those overflowed the row and the "Import my own"
                button was clipping off-screen. We now:
                  • Stack the whole footer vertically on <sm screens (dots
                    on top, buttons below).
                  • Let the button group wrap and stretch full-width on the
                    fork slide so both CTAs are always fully visible.
                  • Keep the tight horizontal layout on md+ where there is
                    plenty of room. */}
            <div
              className="px-5 md:px-7 pb-5 md:pb-6 pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-border/40 sticky bottom-0 bg-background md:static"
              data-testid="welcome-tour-footer"
            >
              <div className="flex items-center gap-1.5" data-testid="welcome-tour-dots">
                {SLIDES.map((s, i) => (
                  <span
                    key={s.id}
                    className={`h-1.5 rounded-full transition-all ${
                      i === index ? 'w-6 bg-primary' : i < index ? 'w-1.5 bg-primary/40' : 'w-1.5 bg-border'
                    }`}
                  />
                ))}
                <span className="text-[11px] text-muted-foreground ml-2 tabular-nums">{index + 1} / {total}</span>
              </div>
              <div
                className={`flex flex-wrap items-stretch sm:items-center gap-2 ${
                  isLast && slide.forkDemoOrImport
                    ? 'w-full sm:w-auto flex-col sm:flex-row'
                    : ''
                }`}
              >
                {index > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={goBack}
                    className="rounded-xl md:h-10 w-full sm:w-auto justify-center"
                    data-testid="welcome-tour-back"
                  >
                    <ArrowLeft className="h-4 w-4 md:mr-1" />
                    <span className="hidden md:inline">Back</span>
                    <span className="md:hidden">Back</span>
                  </Button>
                )}
                {!isLast ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        // v3.7.3 Phase 84.28.11 — Skip tour. Marks the tour
                        // complete on the profile (best-effort) so it
                        // doesn't reopen on the next route change, and
                        // tells the user how to replay it. `completeAndClose`
                        // handles the toast + navigation + localStorage.
                        toast.info(
                          'Tour skipped. You can always replay it from Settings → Help & Orientation.',
                          { duration: 6000 },
                        );
                        await completeAndClose();
                      }}
                      className="rounded-xl md:h-10 w-full sm:w-auto justify-center text-muted-foreground"
                      data-testid="welcome-tour-skip"
                    >
                      Skip tour
                    </Button>
                    <Button
                      size="sm"
                      onClick={goNext}
                      className="rounded-xl md:h-10 w-full sm:w-auto justify-center"
                      data-testid="welcome-tour-next"
                    >
                      Next <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </>
                ) : slide.forkDemoOrImport ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        // v3.7.3 Phase 84.28.11 — instead of seeding the
                        // demo directly from the tour, send the user to
                        // Settings → Help & Orientation where the "Try
                        // with demo data" toggle lives. Gives them the
                        // full explanation before committing to a seed,
                        // and matches the Help card's affordances.
                        setCompleting(true);
                        try {
                          const localKey = `${STORAGE_KEY_PREFIX}${user.id}`;
                          window.localStorage.setItem(localKey, new Date().toISOString());
                        } catch { /* private mode etc. */ }
                        setOpen(false);
                        try {
                          const { data: { session } } = await supabase.auth.getSession();
                          const token = session?.access_token;
                          if (token) {
                            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/welcome-tour-complete`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            });
                            refreshProfile?.();
                          }
                        } catch { /* best-effort */ }
                        setCompleting(false);
                        navigate('/settings?section=help');
                      }}
                      disabled={completing}
                      className="rounded-xl md:h-10 w-full sm:w-auto justify-center"
                      data-testid="welcome-tour-try-demo"
                    >
                      Try with demo data
                    </Button>
                    <Button
                      size="sm"
                      onClick={completeAndClose}
                      disabled={completing || seedingDemo}
                      className="rounded-xl md:h-10 w-full sm:w-auto justify-center"
                      data-testid="welcome-tour-import-own"
                    >
                      {completing ? 'Starting…' : 'Import my own statement'}
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    onClick={completeAndClose}
                    disabled={completing}
                    className="rounded-xl md:h-10 w-full sm:w-auto justify-center"
                    data-testid="welcome-tour-finish"
                  >
                    {completing ? 'Starting…' : (slide.finalCta || 'Get started')}
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default WelcomeTour;
